import { MeasurementSystem } from '@fleet-bffs/api-contract';
import AssetTypeConstants from '~/constants/asset-type-constants';
import ModalStep from '~/domain/add-asset/data/modal-step';
import { RoboticMowerBleConnectionStatus } from './ble-connection-status';
import type { RoboticMowerSitemapState } from './mower-sitemap-state';
import { NativeAppInfo } from './native-version';

export enum WebCommandEnum {
  ActivateWebView = 'ActivateWebView',
  InitializeWebView = 'InitializeWebView',
  Navigate = 'Navigate',
  OpenProductList = 'OpenProductList',
  ReceiveRefreshTokenResponse = 'ReceiveRefreshTokenResponse',
  RefreshAssetDetails = 'RefreshAssetDetails',
  RefreshDashboard = 'RefreshDashboard',
  RefreshInventory = 'RefreshInventory',
  RequestAppLoaded = 'RequestAppLoaded',
  RoboticMowerConnectionUpdated = 'RoboticMowerConnectionUpdated',
  RoboticMowerSitemapUpdated = 'RoboticMowerSitemapUpdated',
  ClearServiceWorkerCaches = 'ClearServiceWorkerCaches',
  UserMeasurementSystemUpdated = 'UserMeasurementSystemUpdated',
}
export enum NativeCommandEnum {
  /**
   * Add asset from native (display pop-up)
   */
  AddAssetNative = 'AddAssetNative',
  AnalyticsLogEvent = 'AnalyticsLogEvent',
  AnalyticsSetUserId = 'AnalyticsSetUserId',
  AnalyticsSetUserProperty = 'AnalyticsSetUserProperty',
  AppInitialized = 'AppInitialized',
  AppLoaded = 'AppLoaded',
  /**
   * Open AMC/Fleet app to continue with EPOS installation
   */
  InitiateEposInstallation = 'InitiateEposInstallation',
  LogInNative = 'LogInNative',
  /**
   * Navigate to a web path
   */
  NavigateTo = 'NavigateTo',
  /**
   * Logout from app
   */
  NavigateToLogin = 'NavigateToLogin',
  /**
   * Navigate to a Maui path
   */
  NavigateToMaui = 'NavigateToMaui',
  /**
   * Open AMC/Fleet app to continue with EPOS installation
   */
  OpenAutoMowerDirect = 'OpenAutoMowerDirect',
  OpenBluetoothSettings = 'OpenBluetoothSettings',
  RefreshToken = 'RefreshToken',
  /**
   * Notify MAUI that a native-requested navigation has been committed by React Router.
   */
  RouteChanged = 'RouteChanged',
  UserMeasurementSystemUpdated = 'UserMeasurementSystemUpdated',
}

export type WebCommand<T extends WebCommandEnum> = {
  name: T;
  params?: T extends WebCommandEnum.InitializeWebView
    ? NativeAppInfo
    : T extends WebCommandEnum.Navigate
    ? {
        path: string;
        search?: string;
        data?: string;
      }
    : T extends WebCommandEnum.OpenProductList
    ? {
        assetType: AssetTypeConstants;
        step: ModalStep;
      }
    : T extends WebCommandEnum.RoboticMowerConnectionUpdated
    ? RoboticMowerBleConnectionStatus
    : T extends WebCommandEnum.RoboticMowerSitemapUpdated
    ? RoboticMowerSitemapState
    : T extends WebCommandEnum.UserMeasurementSystemUpdated
    ? {
        measurementSystem: MeasurementSystem;
      }
    : never;
};

export enum NativeRoutePathsEnum {
  AddConnectivityView = 'AddConnectivityView',
  DiscoverMowersView = 'DiscoverMowersView',
  InstallSensorInstructionsView = 'InstallSensorInstructionsView',
}
export type NativeCommand<T extends NativeCommandEnum> = {
  name: T;
  params?: T extends NativeCommandEnum.NavigateToMaui
    ? {
        path: NativeRoutePathsEnum;
        parameters?: Record<string, unknown>;
      }
    : T extends NativeCommandEnum.NavigateTo
    ? {
        path: string;
        search: string;
        data?: Record<string, any>;
      }
    : T extends NativeCommandEnum.InitiateEposInstallation
    ? {
        serialNumber?: string;
        assetId?: string;
        iprId: string;
        mapObjectId?: string;
      }
    : T extends NativeCommandEnum.OpenAutoMowerDirect
    ? {
        assetId: string;
      }
    : T extends NativeCommandEnum.LogInNative
    ? {
        level: string;
        messages: any[];
      }
    : T extends NativeCommandEnum.AnalyticsLogEvent
    ? {
        eventName: string;
        parameters?: { [key: string]: any };
      }
    : T extends NativeCommandEnum.AnalyticsSetUserId
    ? {
        userId: string;
      }
    : T extends NativeCommandEnum.AnalyticsSetUserProperty
    ? {
        name: string;
        value: string;
      }
    : T extends NativeCommandEnum.OpenBluetoothSettings
    ? {
        assetId: string;
        serialNumber: string;
      }
    : T extends NativeCommandEnum.UserMeasurementSystemUpdated
    ? {
        measurementSystem: MeasurementSystem;
      }
    : never;
};
